<template>
  <div class="admin-container" :style="{ paddingTop: bannerStackHeight + 'px' }">
    <PlatformBanners @height="bannerStackHeight = $event"/>
    <TheSideBar v-if="showContent"/>
    <div
      class="admin-page-container"
      :class="{ 'admin-page-container--has-footer': showContent && userSettings }"
    >
      <AdminNavBar :breadcrumbs="breadcrumbs"/>
      <div id="adminPageBody" class="admin-page-body scroll-y-visible">
        <slot v-if="showContent"/>
      </div>
      <AskClaraFooter v-if="showContent && userSettings"/>
    </div>
    <InsuranceNotifications v-if="userSettings"></InsuranceNotifications>
    <OnboardingComponent v-if="insuranceConfig"></OnboardingComponent>
    <ClaraAIPopup v-if="userSettings"/>
    <AskClaraPanel v-if="userSettings && claraOpen"/>
    <WhatsAppPaidPromoModal
      v-if="showWhatsAppPaidPromo"
      @close="showWhatsAppPaidPromo = false"
      @confirm="showWhatsAppPaidPromo = false"
    />
    <SubscriptionExpiredModal
      v-if="isSubscriptionExpired"
      @close="isModalDismissed = true"
    />
    <div class="page-preloader" v-if="pageLoading">
      <BasePreloader :size="'l'" :color="'purple'"/>
    </div>
  </div>
</template>

<script>
import TheSideBar from '@/views/RenovaDashboard/CommonParts/SideBar/TheSideBar';
import AdminNavBar from '@/views/RenovaDashboard/CommonParts/AdminNavBar';
import PlatformBanners from '@/views/RenovaDashboard/CommonParts/PlatformBanners';
import SubscriptionExpiredModal from '@/views/RenovaDashboard/CommonParts/SubscriptionExpiredModal';
import {USER_REQUEST} from '@/store/actions/user';
import {AUTH_LOGOUT} from '@/store/actions/auth';
import {GET_ACTIVE_INSURANCE_COMPANIES, GET_DICTIONARY} from '@/store/actions/dictionary';
import {TICKET_LIST_FILTERS_REQUEST, TICKET_LIST_REQUEST} from '@/store/actions/ticketList';
import axios from 'axios';
import {mapGetters} from 'vuex';
import Vue from 'vue';
import InsuranceNotifications from '@/views/RenovaDashboard/CommonParts/InsuranceNotifications';
import growthbook from '@/utils/growthbook';
import featureMixin from '@/mixins/featureMixin';
import visibilityMixin from '@/mixins/visibilityMixin';
import OpenreplayTracker from '@/utils/openreplay';
import amplitude, {logAmplitudeEvent} from '@/utils/amplitude';
import {identifyPosthogUser, initPosthog} from '@/utils/posthog';
import OnboardingComponent from '@/views/RenovaDashboard/OnboardingParts/Onboarding';
import ClaraAIPopup from '@/views/RenovaDashboard/CommonParts/ClaraAIPopup';
import AskClaraFooter from '@/views/RenovaDashboard/AskClaraParts/AskClaraFooter';
import AskClaraPanel from '@/views/RenovaDashboard/AskClaraParts/AskClaraPanel';
import WhatsAppPaidPromoModal from '@/views/RenovaDashboard/CommonParts/WhatsAppPaidPromoModal';
import {INSURANCE_DETAILS_REQUEST} from '@/store/actions/onboarding';
import {USER_ROLES} from '@/store/modules/user';
import {setIntercomUserInfo} from '@/utils/intercom';
import {PROGRAMS_LIST_REQUEST} from '@/store/actions/programs';
import {FETCH_NOTIFICATION_BADGES} from '@/store/actions/notifications';
import {FETCH_PLATFORM_ALERTS} from '@/store/actions/platformAlerts';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
// eslint-disable-next-line
import licenseMixin from '@/mixins/licenseMixin';

/**
 * @emits breadcrumbs, add-breadcrumb, click-breadcrumb
 * @type {Vue | CombinedVueInstance<Vue, object, object, object, Record<never, any>>}
 */
const adminEventBus = new Vue(); // для новой админки Renova

// A lapsed subscription is not urgent to the minute, and the endpoint is a handful of local
// queries — five minutes keeps a stale banner from outliving a payment by long without making
// this a hot path.
const PLATFORM_ALERT_POLL_MS = 5 * 60 * 1000;
export {adminEventBus};

export default {
  name: 'RenovaDashboardLayout',
  components: {
    BasePreloader,
    OnboardingComponent,
    AdminNavBar,
    TheSideBar,
    InsuranceNotifications,
    PlatformBanners,
    SubscriptionExpiredModal,
    ClaraAIPopup,
    AskClaraFooter,
    AskClaraPanel,
    WhatsAppPaidPromoModal,
  },
  data() {
    return {
      showContent: false,
      pageLoading: true,
      breadcrumbs: [],
      isModalDismissed: false,
      showWhatsAppPaidPromo: false,
      // Driven by PlatformBanners' measured height; pushes the whole layout down.
      bannerStackHeight: 0,
      platformAlertsTimer: null,
    };
  },
  watch: {
    $route(to, from) {
      if (to.path !== from.path) {
        this.breadcrumbs = [];
      }
    },
  },
  async created() {
    this.pageLoading = true;
    // set breadcrumbs
    adminEventBus.$on('breadcrumbs', (breadcrumbs) => {
      this.breadcrumbs = breadcrumbs;
    });
    // add new breadcrumb to the end
    adminEventBus.$on('add-breadcrumb', (title, link) => {
      const breadcrumbAlreadyExists = this.breadcrumbs.some(
        (breadcrumb) => breadcrumb.title === title
      );
      if (!breadcrumbAlreadyExists) {
        this.breadcrumbs.push({
          title,
          link,
        });
      }
    });
    // remove all breadcrumbs after clicked
    adminEventBus.$on('click-breadcrumb', (index) => {
      this.breadcrumbs.splice(index + 1);
    });
    adminEventBus.$on('open-whatsapp-promo', this.checkWhatsAppPaidPromo);
    if (!localStorage.getItem('notify-sound')) localStorage.setItem('notify-sound', JSON.stringify(true));
    this.isModalDismissed = false;

    this.$store.dispatch(USER_REQUEST, localStorage.getItem(`${this.user.id}_insuranceProvider`))
      .then(async () => {
        this.setProvider(this.user);
        initPosthog();
        this.initPosthog(this.user);
        this.initAmplitude(this.user);
        this.initIntercom(this.user);
        this.initGrowthbook({
          company: localStorage.getItem(`${this.user.id}_insuranceProvider`) || null,
          id: this.user.id,
        });
        this.initOpenreplay(this.user);
        // amplitude.init('5c0e7ca0715ba0643356491c582582a3', this.user.id);
        if (!this.user.domains?.length) {
          this.$store.dispatch(AUTH_LOGOUT)
            .then(() => {
              this.$router.push('/');
            });
        } else {
          await Promise.all([
            this.$store.dispatch('fetchAllPoliciesTotalCount'),
            this.$store.dispatch('fetchLeadTotalCount'),
          ]);
          this.pageLoading = false;
          this.handlePostBillingRedirect();
          this.$store.dispatch(GET_DICTIONARY, this.user.id);
          this.$store.dispatch(GET_ACTIVE_INSURANCE_COMPANIES);
          this.$store.dispatch(TICKET_LIST_FILTERS_REQUEST);
          this.$store.dispatch(PROGRAMS_LIST_REQUEST);
          this.fetchTickets();
          this.$store.dispatch('fetchTags');
          this.$store.dispatch(FETCH_NOTIFICATION_BADGES);
          this.fetchPlatformAlerts();
        }
      });
    this.getUserSetting();
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response.status === 401) {
          // Exception for 401 error from openai, will ignore it
          if (!error.request?.responseURL?.contains('openai')) {
            this.$store.dispatch(AUTH_LOGOUT)
              .then(() => {
                this.$router.push('/login');
              });
          }
        }

        // Session flip: a non-admin operator's workspace went seats_exceeded mid-session.
        // The backend returns 403 with code ACCOUNT_DEACTIVATED; log them out immediately.
        if (error.response.status === 403 && error.response.data?.code === 'ACCOUNT_DEACTIVATED') {
          this.$store.dispatch(AUTH_LOGOUT)
            .then(() => {
              this.$router.push('/login');
            });
        }

        if (error.response.data?.code === 'LICENSE_LIMIT_EXCEEDED') {
          this.$notify.error({
            title: this.$t('subscription.licenseLimitExceeded2'),
            dangerouslyUseHTMLString: true,
            message: `${error.response.data.message.replace(/\/payment/g, '<a href="/payment" class="body-text body-text--link color--purple">Payment page</a>')}`,
            duration: 5000,
          });
        }

        return Promise.reject(error);
      }
    );
  },
  mounted() {
    this.setupStyles();
    // Billing state changes arrive from Stripe as a webhook, with no websocket event of its own,
    // so a lapse would otherwise only appear on a page reload. WhatsApp drops DO push an event —
    // InsuranceNotifications refetches on those — and this poll is the floor under both.
    this.platformAlertsTimer = setInterval(this.fetchPlatformAlerts, PLATFORM_ALERT_POLL_MS);
    // Payment happens in another tab; the same reason IntegrationsView refreshes on return.
    document.addEventListener('visibilitychange', this.onVisibilityChangeForAlerts);
  },
  beforeDestroy() {
    if (this.platformAlertsTimer) clearInterval(this.platformAlertsTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChangeForAlerts);
  },
  mixins: [featureMixin, visibilityMixin, licenseMixin],
  computed: {
    ...mapGetters(['user', 'userRole', 'ticketList', 'selectedInsuranceProviderDetails', 'userSettings', 'insuranceConfig', 'providerType', 'leadTotalCount']),
    ...mapGetters('Clara', ['claraOpen']),
    isSubscriptionExpired() {
      return this.user?.accessLocked && !this.isModalDismissed;
    },
    // Drives .admin-container--has-banner padding so the banner doesn't overlap content.
    isUserAdmin() {
      return this.userRole === USER_ROLES.ADMIN;
    },
  },
  methods: {
    fetchPlatformAlerts() {
      // Swallowed on purpose: a banner that cannot be fetched must never surface as a toast, and
      // the store keeps the previous alerts rather than blanking the strip on a dropped request.
      this.$store.dispatch(FETCH_PLATFORM_ALERTS).catch(() => {});
    },
    onVisibilityChangeForAlerts() {
      if (document.visibilityState === 'visible') this.fetchPlatformAlerts();
    },
    fetchTickets() {
      this.$store.dispatch(TICKET_LIST_REQUEST)
        .then((resp) => {
          this.newMessagesNotify();
          if (this.user?.id) {
            this.newTicketsNotify(resp.totalCount);
          }
        });
    },
    newTicketsNotify(totalCount) {
      const tickets = JSON.parse(localStorage.getItem(`tickets.${this.user.id}`));
      if (!tickets || totalCount > tickets?.count) {
        localStorage.setItem(
          `tickets.${this.user.id}`,
          JSON.stringify({
            count: totalCount,
          })
        );
        if (tickets && JSON.parse(localStorage.getItem('notify-sound'))) {
          const audio = new Audio('/sounds/new-ticket.mp3');
          audio.oncanplaythrough = () => {
            const playedPromise = audio.play();
            if (playedPromise) {
              playedPromise.catch((e) => {
                console.error(e.name);
              });
            }
          };
        }
      }
    },
    async initGrowthbook(attributes) {
      growthbook.setAttributes(attributes);
      await growthbook.loadFeatures();
      this.showContent = true;
    },
    initAmplitude(user) {
      if (!window.isDev) {
        amplitude.getInstance()
          .init('58096cb94b277fcccd492e4d658bad12', user.id);
        amplitude.setUserProperties({
          email: user.email,
          ProviderId: this.selectedInsuranceProviderDetails.id,
          ProviderName: this.selectedInsuranceProviderDetails.name,
          ProjectName: `${window.whiteLabelCompany}-admin`,
        });
      }
    },
    initPosthog(user) {
      identifyPosthogUser(user, this.selectedInsuranceProviderDetails);
    },
    initIntercom(user) {
      setIntercomUserInfo(user);
    },
    handlePostBillingRedirect() {
      if (!localStorage.getItem('pending_post_billing_redirect')) return;
      localStorage.removeItem('pending_post_billing_redirect');
      // Redirect to the hub page with no data (the newly purchased module)
      // A count we could not fetch is not a count of zero — don't send the user to an
      // "empty hub" page on the strength of a failed request.
      const policyCount = this.$store.getters.allPoliciesTotalCount;
      const leadCount = this.$store.getters.leadTotalCount;
      if (policyCount === 0 && this.$store.getters.providerType !== 'sales_hub') {
        this.$router.push('/admin/policies');
      } else if (leadCount === 0 && this.$store.getters.providerType !== 'service_hub') {
        this.$router.push('/admin/leads');
      }
    },
    initOpenreplay(user) {
      OpenreplayTracker.start({
        userID: user.id,
      });
      if (user.clientCompanyDetails) {
        OpenreplayTracker.setMetadata('insurance', user.clientCompanyDetails.id);
        OpenreplayTracker.setMetadata('insuranceName', user.clientCompanyDetails.name);
      }
    },
    async checkWhatsAppPaidPromo() {
      if (!this.isSalesHubEnabled) {
        return;
      }
      const isConnected = await this.checkIfWhatsAppPaidIsConnected();
      if (isConnected) return;
      this.showWhatsAppPaidPromo = true;
    },
    async checkIfWhatsAppPaidIsConnected() {
      try {
        const payload = {
          personProfileId: this.user?.profile?.profileId,
        };

        const res = await axios.post('/insurance/integrationschannel/list', payload);
        const list = Array.isArray(res.data?.items) ? res.data.items : [];
        const paid = list.find((i) => i?.channelProvider === 'WHATSAPP_UNOFFICIAL_PAID');
        const total = Number(paid?.quota?.totalChannelsPurchased || 0);
        return total > 0;
      } catch (err) {
        console.error('Failed to check WhatsApp Paid status', err);
        return false;
      }
    },
    newMessagesNotify() {
      const newTodoTicketsCount = this.ticketList.stats.commonCountTodo;
      const leadOnboardingActive = this.providerType === 'sales_hub' && !this.leadTotalCount;
      if (newTodoTicketsCount && !leadOnboardingActive) {
        document.title = `${newTodoTicketsCount} ${this.$tc(
          'adminTitles.NewMessage',
          newTodoTicketsCount
        )}`;
        this.drawCounterOnFavicon(newTodoTicketsCount);
      } else if (
        !newTodoTicketsCount
        && document.title.includes(this.$tc('adminTitles.NewMessage', newTodoTicketsCount)) || leadOnboardingActive
      ) {
        const title = this.$t(`meta.${this.$router.history.current.name}.title`);
        // eslint-disable-next-line no-template-curly-in-string
        document.title = title.replaceAll('${window.companyName}', window.companyName);
        const favicon = document.getElementById('favicon');
        favicon.href = `/favicons/favicon_${window.whiteLabelCompany}.svg`;
      }
    },
    setProvider(user) {
      const providers = user.domains;
      const savedProviderId = localStorage.getItem(`${user.id}_insuranceProvider`);
      if (savedProviderId && providers.some((provider) => provider.id === savedProviderId)) {
        axios.defaults.headers.common['X-Insurance-Provider-ID'] = savedProviderId;
        this.$store.dispatch(INSURANCE_DETAILS_REQUEST, savedProviderId);
      } else {
        localStorage.setItem(`${user.id}_insuranceProvider`, providers[0].id);
        axios.defaults.headers.common['X-Insurance-Provider-ID'] = providers[0].id;
        window.location.reload();
      }
    },
    setupStyles() {
      if (window.adminLayout === 'RenovaDashboardLayout') {
        import('@/assets/scss/renova/renova-ui-elements.scss');
      }
    },
    drawCounterOnFavicon(counter) {
      if (!counter) {
        this.resetFavicon();
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const context = canvas.getContext('2d');

      const img = new Image();
      img.onload = () => {
        context.drawImage(img, 0, 0, 32, 32);
        context.fillStyle = '#FF8D7A';
        const circleSize = 16;
        context.beginPath();
        context.arc(23, 23, circleSize / 2, 0, 2 * Math.PI);
        context.fill();
        context.fillStyle = '#FFFFFF';
        context.font = `normal ${counter >= 100 ? '8' : '12'}px Arial`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(counter, 23, 23 + 1);
        const favicon = document.getElementById('favicon');
        favicon.href = canvas.toDataURL('image/x-icon');
      };

      img.src = `/favicons/favicon_${window.whiteLabelCompany}.svg`;
    },
    getUserSetting() {
      setTimeout(() => {
        this.$store.dispatch('fetchUserSettings', this.selectedInsuranceProviderDetails.id);
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/renova/fonts.scss";
@import "@/assets/scss/renova/renova-admin.scss";

* {
  font-family: "Inter", serif;
}

.admin-container {
  display: flex;
  flex-direction: row;
  height: 100vh;
  box-sizing: border-box;
  //background: var(--solid-light-purple);
}

// The layout is pushed down by the banner stack's MEASURED height (an inline padding-top driven
// by PlatformBanners), not by a hardcoded figure: there can be several strips now, and one can
// wrap to two lines on a narrow window. box-sizing matters because .admin-container is 100vh —
// without it that padding is added to the viewport height and the sidebar's bottom is pushed
// off-screen.

.admin-page-container {
  width: 100%;
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

// AskClaraFooter's 40px box plus its 1px top border.
$admin-footer-height: 41px;
// Breathing room under a horizontal scrollbar so it never sits flush on the footer's edge.
$scroll-x-gutter: 12px;

// The kanban boards size themselves off `100vh` rather than off their container, so they have
// to subtract by hand whatever else the column holds. Before this they ran past the bottom of
// the page body by exactly the footer's height and their scrollbar was pushed out of view.
// Without the footer only the gutter applies, which is what the original figures assumed.
.admin-page-container {
  --admin-board-inset: #{$scroll-x-gutter};
  // Read by StickyScrollX, which works out the rest from the scroller it actually sits in.
  --scroll-x-gutter: #{$scroll-x-gutter};
}

.admin-page-container--has-footer {
  --admin-board-inset: #{$admin-footer-height + $scroll-x-gutter};
}

.admin-page-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto; // scroll
  // Explicit, because an unset `overflow-x` computes to `auto` next to `overflow-y: auto` and
  // would make this a second horizontal scroller, competing with the StickyScrollX the wide
  // content already scrolls inside.
  overflow-x: hidden;
  // Hold the bar's gutter whether or not the page currently overflows, so applying a filter
  // that leaves three rows does not shift every column sideways as the bar disappears.
  scrollbar-gutter: stable;
  // The bar is drawn *into* the right-hand page gutter rather than beside it: take its width
  // out of the padding so the content box stays the width the pages were written against —
  // a dozen of their headers size themselves as `calc(100vw - 336px)`, i.e. the sidebar plus
  // 48px of padding either side, and would otherwise hang past the right edge by a scrollbar.
  // `--native-scrollbar-size` is measured by `@/utils/scrollbars`.
  padding: 32px calc(48px - var(--native-scrollbar-size, 10px)) 100px 48px;
}

.page-preloader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background: #FFFFFF;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000000;
}

@media screen and (max-width: 668px) {
  .admin-container {
    flex-direction: column;
    height: auto;
  }
  .admin-page-body {
    overflow: visible;
    padding: 0 20px 20px 20px;
  }
}
</style>
